// src/components/padrekko/qc/QcInspector.jsx
//
// Per-pad working view.
//   - Run header (title, "Inspection run · N of M pads", exit X)
//   - Progress bar
//   - Pad header (name, centroid, state badge)
//   - Error banner (renders only when useQcStore.error is set — covers
//     session-load AND analyze failures; dismiss via clearError)
//   - Findings section: QcPolygonDrawer + (eventually) QcFindingCard list
//   - Skip / Finish buttons (local navigation only; server wiring in #8)
//
// QcPolygonDrawer's own in-progress status row ("Click vertices · right-
// click to finish" + Cancel button) is the panel-level "you're mid-draw"
// affordance — visible to an operator about to tab away.
//
// Correct/finish→summarize lands in #8; summary view + export land in #9.

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, SkipForward, Check, X, AlertCircle, Loader2, ArrowLeft, RotateCcw } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import { toast } from "../../../stores/useToastStore";
import { useAppNav } from "../../../hooks/useAppNav";
import { useAppViewStore } from "../../../stores/useAppViewStore";
import StateBadge from "./StateBadge";
import QcPolygonDrawer from "./QcPolygonDrawer";
import QcFindingsLayer from "./QcFindingsLayer";
import QcCurrentPadLayer from "./QcCurrentPadLayer";
import QcResolvedPadLayer from "./QcResolvedPadLayer";
import { resolutionCopy } from "./resolutionCopy";
import QcFindingCard from "./QcFindingCard";
import AttachmentSection from "./AttachmentSection";
import { PadCounts } from "./QcCountsSummary";
import { findingKey } from "../../../stores/useQcStore";

// Centroid label — "lat, lon" at 4 decimal places, per v0 convention.
function centroidLabel(bbox) {
  if (!bbox) return "(no bbox)";
  const lon = (Number(bbox.east) + Number(bbox.west)) / 2;
  const lat = (Number(bbox.north) + Number(bbox.south)) / 2;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return "(no bbox)";
  return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
}

/**
 * What the current pad was analyzed on, and what to do about it.
 *
 * THE CONTROL IS GATED ON caseWriteable, not on the server refusing. Resolving
 * persists the witness columns, so the server gates it on the stage writer; a
 * button rendered to a non-writer is a control that 403s, which is the trap
 * caseStages.js:119-133 names — the gate allows, a sibling refuses, and the
 * user is shown something that fails. A reader still SEES the banner, because
 * reads are not stage-gated: a respondent who cannot re-resolve can still see
 * what the producer resolved.
 */
function GeometryResolutionBanner({ pad, writeable, inflight, onResolve }) {
  const copy = resolutionCopy(pad);

  const TONE = {
    resolved:  "border-teal-500/40 bg-teal-500/5",
    attention: "border-amber-500/40 bg-amber-500/5",
    neutral:   "border-slate-600/50 bg-slate-500/5",
  };

  // Never resolved. Offer it to whoever may, say nothing to whoever may not —
  // an empty state explaining a button they cannot press is noise.
  if (!copy) {
    if (!writeable) return null;
    return (
      <button
        type="button"
        onClick={onResolve}
        disabled={inflight}
        className="mt-2 w-full text-left px-2 py-1.5 rounded border border-slate-600/50
                   bg-slate-500/5 text-[10px] text-fg-secondary
                   hover:border-slate-500 hover:text-gray-300 disabled:opacity-50"
      >
        {inflight ? "Resolving pad geometry…" : "Resolve pad geometry"}
      </button>
    );
  }

  return (
    <div className={`mt-2 px-2 py-1.5 rounded border ${TONE[copy.tone]}`}>
      {/* Title and "Resolve again" share the first row: the control no longer
          takes a line of its own. The title wraps if it must; the control
          never does. */}
      <div className="flex items-start justify-between gap-2" data-resolution-title-row>
        <div className="min-w-0 flex-1 text-[10px] font-medium text-gray-200">{copy.title}</div>
        {writeable && (
          <button
            type="button"
            onClick={onResolve}
            disabled={inflight}
            className="shrink-0 whitespace-nowrap text-[10px] text-teal-300 hover:text-teal-200 disabled:opacity-50"
          >
            {inflight ? "Resolving…" : "Resolve again"}
          </button>
        )}
      </div>
      {copy.detail && (
        <div className="text-[10px] text-fg-secondary font-mono tabular-nums mt-0.5">
          {copy.detail}
        </div>
      )}
      {copy.action && (
        <div className="text-[10px] text-fg-secondary mt-1">{copy.action}</div>
      )}
    </div>
  );
}

export default function QcInspector({ onBackToSummary = null }) {
  const session              = useQcStore((s) => s.session);
  const currentPadIdx        = useQcStore((s) => s.currentPadIdx);
  const setCurrentPadIdx     = useQcStore((s) => s.setCurrentPadIdx);
  const error                = useQcStore((s) => s.error);
  const clearError           = useQcStore((s) => s.clearError);
  const skipPad              = useQcStore((s) => s.skipPad);
  const finishPad            = useQcStore((s) => s.finishPad);
  const skipInflightPadId    = useQcStore((s) => s.skipInflightPadId);
  const finishInflightPadId  = useQcStore((s) => s.finishInflightPadId);
  const analyzeInflightPadId = useQcStore((s) => s.analyzeInflightPadId);
  const dismissedFindingKeys = useQcStore((s) => s.dismissedFindingKeys);
  const caseInfo             = useQcStore((s) => s.caseInfo);
  const caseWriteable        = useQcStore((s) => s.caseWriteable);
  const resolvePadGeometry   = useQcStore((s) => s.resolvePadGeometry);
  const resolveInflightPadId = useQcStore((s) => s.resolveGeometryInflightPadId);
  const kickoffAutodetect    = useQcStore((s) => s.kickoffAutodetect);
  const retryAutodetect      = useQcStore((s) => s.retryAutodetect);
  const analyzeRemaining     = useQcStore((s) => s.analyzeRemaining);
  const loadCase             = useQcStore((s) => s.loadCase);
  const mapInstance          = useAppViewStore((s) => s.mapInstance);

  // Top-level product/mode (Explore, PadRekko, etc.). MapPage uses a
  // Panel that toggles via opacity/translate, NOT mount/unmount — so
  // QcInspector survives mode switches. We gate the auto-zoom on
  // padrekko-is-active so (a) we don't fire fitBounds while the user
  // is on Explore, and (b) returning to PadRekko after panning in
  // Explore re-fires the zoom (deps flip on the activeTab transition).
  const activeTab = useAppNav().activeTab;
  const isPadrekkoActive = activeTab === "padrekko";

  // useMemo so `pads` reference is stable across renders when session is
  // unchanged — keeps downstream useEffect/useMemo deps from re-firing.
  const pads = useMemo(() => session?.pads ?? [], [session]);
  const padCount = pads.length;

  // Cursor lives in the store so tab switches preserve it. loadSession
  // seeds it via firstActionablePadIdx; we fall back to 0 only if the store
  // somehow lands here with a session but no index (shouldn't happen — the
  // load action sets both atomically).
  const padIdx = currentPadIdx ?? 0;

  const pad = pads[padIdx];
  // Computed server-side by reviewCounts.js and handed over whole.
  const reviewCountsStage  = useQcStore((s) => s.session?.review_counts_stage ?? null);
  const centroid = useMemo(() => centroidLabel(pad?.aoi_bbox), [pad?.aoi_bbox]);

  // Findings the operator hasn't dismissed in this session, sorted by
  // idx ascending (operator's draw order; matches the map's layer order).
  // Dismiss is local-only — server still has every finding; the layer
  // also filters by dismissedFindingKeys so panel and map stay in sync.
  const visibleFindings = useMemo(() => {
    const all = pad?.findings ?? [];
    return all
      .filter((f) => !dismissedFindingKeys.has(findingKey(pad?.id, f.idx)))
      .sort((a, b) => a.idx - b.idx);
  }, [pad?.findings, dismissedFindingKeys, pad?.id]);

  // Auto-zoom to the current pad's AOI bbox. Fires on session load,
  // Skip/Finish advance, Resume, Next/Prev navigation, and mode-switch
  // back to PadRekko (activeTab flips → effect re-runs). Snapping back
  // on mode switch is intentional: "show me the pad I'm working on".
  // Padding/maxZoom/duration match PadLayer.jsx's pad-zoom — same
  // domain, same concept, consistent feel.
  useEffect(() => {
    if (!mapInstance || !pad?.aoi_bbox || !isPadrekkoActive) return;
    const { north, south, east, west } = pad.aoi_bbox;
    // FRAME THE UNION, NOT THE RESOLVED GEOMETRY. The resolved pad can be a
    // sixth of the AOI's area, and zooming to it alone would push the AOI
    // outline off screen — so the analyst would stop being able to see the
    // thing the banner is telling them about. The AOI bounds already contain
    // the resolved geometry on the pad_feature arm (containment is what put it
    // there), so the union is the AOI except when a resolution is stale or a
    // fallback stored something wider; max() covers that without a branch.
    const b = pad.geometry_source === "pad_feature" && Array.isArray(pad.bbox)
      ? [Math.min(west, pad.bbox[0]), Math.min(south, pad.bbox[1]),
         Math.max(east, pad.bbox[2]), Math.max(north, pad.bbox[3])]
      : [west, south, east, north];
    mapInstance.fitBounds(
      [[b[0], b[1]], [b[2], b[3]]],
      {
        padding: { top: 80, bottom: 80, left: 80, right: 80 },
        maxZoom: 17,
        duration: 600,
      }
    );
  }, [mapInstance, pad?.id, pad?.geometry_source, isPadrekkoActive]);

  // Previous + Next = pure cursor navigation (no server, no state
  // mutation). Skip + Finish are store actions that PATCH/POST then
  // advance on success — that cursor advance lives in the action.
  //
  // Why a Next button at all: terminal pads (skipped/completed) disable
  // Skip + Finish, but the operator still needs to move past them.
  // Without Next, terminal pads trap forward navigation. Next preserves
  // the audit-trail discipline — moving on without committing isn't a
  // "skip" event, just navigation.
  function back() {
    if (padIdx > 0) setCurrentPadIdx(padIdx - 1);
  }
  function forward() {
    if (padIdx + 1 < padCount) setCurrentPadIdx(padIdx + 1);
  }

  const isFirstPad = padIdx === 0;
  const isLastPad  = padIdx + 1 >= padCount;

  // Three operations, one-at-a-time per pad. The store actions enforce
  // this too, but reading the flags here lets us mute the buttons and
  // show the right inflight UI without an extra round trip.
  const isSkippingThisPad   = pad && skipInflightPadId === pad.id;
  const isFinishingThisPad  = pad && finishInflightPadId === pad.id;
  const isAnalyzingThisPad  = pad && analyzeInflightPadId === pad.id;

  // Day 4 — autodetect state for this pad. detection_status flows
  // through session.pads via loadCase + SSE refresh.
  const detectionStatus = pad?.detection_status ?? "none";
  const detectionError  = pad?.detection_error ?? null;
  const isDetectingThisPad =
    detectionStatus === "pending" || detectionStatus === "running";
  const isDetectionFailed = detectionStatus === "failed";

  const anyInflightOnThisPad =
    isSkippingThisPad || isFinishingThisPad || isAnalyzingThisPad || isDetectingThisPad;

  // Day 4 — single-fire kickoff guard. Tracks pad ids we've already
  // posted the autodetect/start for in this session so we don't
  // double-fire across re-renders. Retry path bypasses by clearing
  // the entry first.
  const kickedOffPadIds = useRef(new Set());
  useEffect(() => {
    if (!pad?.id) return;
    if (kickedOffPadIds.current.has(pad.id)) return;
    if (detectionStatus !== "none") return;
    if (!caseInfo?.config?.autodetect_enabled) return;
    kickedOffPadIds.current.add(pad.id);
    const padName = pad.pad_name;
    // Layer 3 — a failed kickoff must not leave the pad silently at 'none'.
    // Surface it and clear the single-fire guard so re-navigation / "Analyze
    // remaining" can re-attempt. 409 (not eligible — already dispatched in a
    // race) is benign; don't alarm on it.
    kickoffAutodetect(pad.id).then((res) => {
      if (res && res.ok === false && res.status !== 409) {
        kickedOffPadIds.current.delete(pad.id);
        toast.error(
          `Couldn't start autodetect for “${padName ?? "this pad"}”` +
          (res.error ? `: ${res.error}` : ". It remains unanalyzed — use “Analyze remaining” to retry.")
        );
      }
    });
  }, [pad?.id, pad?.pad_name, detectionStatus, caseInfo?.config?.autodetect_enabled, kickoffAutodetect]);

  // Day 4 — fallback polling. While any pad in the session is in
  // 'pending' or 'running', refresh the case every 5s. Belt-and-
  // suspenders for users without the rail SSE subscription active.
  // anyPadDetecting wrapped in useMemo with a stable scalar dep so
  // the interval doesn't tear down + re-arm on every render.
  const anyPadDetecting = useMemo(
    () => pads.some(
      (p) => p.detection_status === "running" || p.detection_status === "pending"
    ),
    [pads]
  );

  // Layer 2 — incomplete-run signal. The cascade can go idle with pads still at
  // 'none' (a stalled/interrupted run), which anyPadDetecting alone would
  // present as "finished". Debounce so a normal between-pad idle blip (the
  // cascade re-arms in well under this window) never flashes the banner.
  const autodetectEnabled = caseInfo?.config?.autodetect_enabled === true;
  const undispatchedCount = useMemo(
    () => pads.filter((p) => (p.detection_status ?? "none") === "none").length,
    [pads]
  );
  const [stalledIncomplete, setStalledIncomplete] = useState(false);
  useEffect(() => {
    if (!autodetectEnabled || anyPadDetecting || undispatchedCount === 0) {
      // Not (yet) stalled — clear on the next tick so the reset isn't a
      // synchronous setState in the effect body.
      const clear = setTimeout(() => setStalledIncomplete(false), 0);
      return () => clearTimeout(clear);
    }
    const t = setTimeout(() => setStalledIncomplete(true), 6000);
    return () => clearTimeout(t);
  }, [autodetectEnabled, anyPadDetecting, undispatchedCount]);
  useEffect(() => {
    if (!anyPadDetecting) return;
    if (!caseInfo?.id) return;
    // Same cursor-preservation rule as the SSE handler: respect
    // auto_advance_on_complete. Default behavior is lazy-per-pad
    // (preserve cursor), opt-in to cascade via case config.
    const autoAdvance = caseInfo.config?.auto_advance_on_complete === true;
    const interval = setInterval(
      () => loadCase(caseInfo.id, { preserveCursor: !autoAdvance }),
      5000
    );
    return () => clearInterval(interval);
  }, [anyPadDetecting, caseInfo?.id, caseInfo?.config?.auto_advance_on_complete, loadCase]);

  // Retry handler — clears the kickoff-guard for this pad so the
  // useEffect above will re-fire on detection_status flip to 'none'
  // (which happens server-side when retry succeeds). Direct call to
  // retryAutodetect short-circuits the wait — re-runs immediately.
  async function handleRetryAutodetect() {
    if (!pad?.id) return;
    kickedOffPadIds.current.delete(pad.id);
    await retryAutodetect(pad.id);
  }

  // Layer 2 — manual recovery for a stalled cascade: dispatch every pad still
  // at detection_status='none'. Clears the single-fire guards so the cascade
  // can re-arm, then surfaces the outcome.
  async function handleAnalyzeRemaining() {
    kickedOffPadIds.current.clear();
    const res = await analyzeRemaining();
    if (!res?.ok) {
      toast.error(
        `Analyze remaining: ${res?.failed ?? "some"} of ${res?.total ?? "the"} pad(s) failed to start.`
      );
    } else if (res.dispatched > 0) {
      toast.info(`Analyzing ${res.dispatched} remaining pad${res.dispatched === 1 ? "" : "s"}…`);
    }
  }

  // Terminal-state guard. Server returns 409 on these, so disabling here
  // saves a round trip and gives the operator a clearer signal than a
  // surprise error banner. Both transitions are blocked: skipped and
  // completed reject any further action.
  const isTerminalPad =
    pad?.status === "skipped" || pad?.status === "completed";

  // Phase 6 — close lockdown. The backend 403s every write at 'complete', and
  // the customer close does NOT require all pads terminal, so a completed case
  // can still hold non-terminal pads. Gate Skip/Finish explicitly so they're
  // never offered (dead buttons) on a closed case.
  const isCaseComplete = caseInfo?.current_stage === "complete";

  const canSkip   = !!pad && !anyInflightOnThisPad && !isTerminalPad && !isCaseComplete;
  const canFinish = !!pad && !anyInflightOnThisPad && !isTerminalPad && !isCaseComplete;

  // Defensive — session can be cleared between render and this point.
  if (!session || !pad) {
    return (
      <div className="p-4 text-[11px] text-fg-secondary">
        No active session.
      </div>
    );
  }


  return (
    <div className="flex flex-col relative">

      {/* Map-side renderers. Both render null and drive the map via
          effects; mount-order is render-order so the current-pad outline
          + label sit beneath the findings layer (findings draw on top). */}
      <QcCurrentPadLayer />
      <QcResolvedPadLayer />
      <QcFindingsLayer />

      {/* Sticky context header — pins pad context to the top of
          PadRekkoPanel's scroll body so the operator never loses reference
          while scrolling through findings. Case context (identity, stage,
          stage actions, progress through pads, exit) is NOT here: it is
          case-scoped and lives in QcCaseBar above the map. The scroll container
          is the panel parent (PadRekkoPanel: `flex-1 overflow-y-auto`),
          not QcInspector itself, so `top-0` here anchors just below the
          tab bar. z-20 sits above scroll content but below the
          isDetectingThisPad overlay (z-50). Soft downward shadow signals
          "more content below" when findings scroll under.
          isDetectionFailed banner is included so blocking failures
          remain visible during scroll. The error banner and findings
          section scroll normally below. */}
      <div className="sticky top-0 z-20 bg-slate-900 shadow-[0_4px_8px_-6px_rgba(0,0,0,0.6)]">
        {/* Day 4 — autodetect failure banner. Sits above the pad header
            so the operator sees it before pad context. */}
        {isDetectionFailed && (
          <div className="px-4 py-2 border-b border-red-500/40 bg-red-500/10">
            <div className="flex items-start gap-2">
              <AlertCircle size={12} className="text-red-300 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-red-200">
                  Autodetect failed
                </div>
                {detectionError && (
                  <div className="text-[9px] text-red-300/80 mt-0.5 leading-snug break-words">
                    {detectionError}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleRetryAutodetect}
                className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                           border-red-400/60 text-red-200 hover:bg-red-500/20 transition shrink-0"
                title="Re-run autodetect for this pad"
              >
                <RotateCcw size={10} />
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Layer 2 — incomplete-run banner. The cascade stopped before analyzing
            every pad; never present this as finished. Amber (informational,
            recoverable) vs the red failure banner above. */}
        {stalledIncomplete && (
          <div className="px-4 py-2 border-b border-amber-500/40 bg-amber-500/10">
            <div className="flex items-start gap-2">
              <AlertCircle size={12} className="text-amber-300 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-amber-200">
                  {undispatchedCount} of {padCount} pad{padCount === 1 ? "" : "s"} not yet analyzed
                </div>
                <div className="text-[9px] text-amber-300/80 mt-0.5 leading-snug">
                  Autodetect stopped before finishing this run.
                </div>
              </div>
              <button
                type="button"
                onClick={handleAnalyzeRemaining}
                className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                           border-amber-400/60 text-amber-200 hover:bg-amber-500/20 transition shrink-0"
                title="Dispatch autodetect for the remaining pads"
              >
                <RotateCcw size={10} />
                Analyze remaining
              </button>
            </div>
          </div>
        )}

        {/* Back-to-summary link — appears only when ReclamationQcTab
            opened this inspector for a completed run (forceInspector
            path). Returns the operator to QcSummary without clearing the
            session. It stays in the panel: it switches which view the
            panel shows, so it is panel-scoped. */}
        {onBackToSummary && (
          <div className="px-4 pt-2 border-b border-slate-800/80 pb-2">
            <button
              type="button"
              onClick={onBackToSummary}
              className="flex items-center gap-1 text-[10px]
                         text-slate-400 hover:text-emerald-300 transition"
              title="Return to the run summary"
            >
              <ArrowLeft size={10} />
              Back to summary
            </button>
          </div>
        )}

      {/* Pad header */}
      <div className="px-4 py-3 border-b border-slate-800/80">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-1">
              Pad {padIdx + 1}
            </div>
            <div className="text-[13px] font-medium text-white truncate">
              {pad.pad_name}
            </div>
            <div className="text-[10px] text-fg-secondary font-mono tabular-nums mt-0.5">
              {centroid}
            </div>
          </div>
          <StateBadge stateCode={pad.state_code} />
        </div>

        <GeometryResolutionBanner
          pad={pad}
          writeable={caseWriteable}
          inflight={resolveInflightPadId === pad.id}
          onResolve={() => resolvePadGeometry(pad.id)}
        />
      </div>
      </div>

      {/* Day 4 — autodetect overlay. Covers the inspector body while
          the pad is in pending/running. Sibling of the sticky wrapper
          (not a descendant) so it positions against QcInspector's
          `relative` and covers the full inspector area, including the
          sticky header above. Findings appear naturally once loadCase
          refreshes the pad row. */}
      {isDetectingThisPad && (
        <div className="absolute inset-0 z-50 bg-slate-900/85 backdrop-blur-sm
                        flex flex-col items-center justify-center gap-3
                        pointer-events-auto">
          <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
          <div className="text-[11px] text-slate-200 max-w-[260px] text-center leading-snug px-4">
            Analyzing pad imagery for compliance issues…
          </div>
          <div className="text-[10px] text-fg-secondary italic">
            {detectionStatus === "pending" ? "Queuing detection" : "AI vision in progress"}
          </div>
        </div>
      )}

      {/* Error banner — single field for now (session-load + analyze);
          v1 may move to typed errors. Pattern mirrors QcPicker's display. */}
      {error && (
        <div className="px-4 py-2 border-b border-slate-800/80">
          <div className="flex items-start gap-2 text-[10px] rounded
                          border border-red-500/60 bg-red-500/15
                          text-red-100 px-2.5 py-1.5 leading-relaxed">
            <AlertCircle size={11} className="mt-[1px] shrink-0" />
            <div className="flex-1">{error}</div>
            <button
              type="button"
              onClick={clearError}
              className="text-red-200/80 hover:text-red-50 transition shrink-0"
              title="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Findings section — drawer + cards. Rendered for every role/stage,
          including the customer at customer_review before the operator's first
          QC pass: the customer's review IS authoritative, so they see and act
          on findings here (Correct / Dismiss / Remove are stage-gated in the
          card). The map's findings layer draws the same polygons, so panel and
          map agree. */}
      <div className="px-4 py-3 border-b border-slate-800/80">
        {/* Pad counts (QcCountsSummary.jsx). The case-level line lives in
            QcCaseBar. Server-computed review coverage — nothing in this
            component counts. */}
        <PadCounts
          findingsCount={visibleFindings.length}
          findings={pad?.findings}
          reviewCounts={pad?.review_counts}
          stage={reviewCountsStage}
          className="mb-2.5"
        />
        <QcPolygonDrawer />
        {visibleFindings.length > 0 && (
          <div className="mt-3 space-y-2">
            {visibleFindings.map((f) => (
              <QcFindingCard
                key={findingKey(pad.id, f.idx)}
                padIdx={padIdx}
                pad={pad}
                finding={f}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pad-level attachments — photos + documents for general pad context
          (things the AI missed). */}
      {pad?.id && (
        <div className="px-4 py-3 border-b border-slate-800/80">
          <AttachmentSection padId={pad.id} compact />
        </div>
      )}

      {/* Pad nav.
          Visual grouping:
            Left  — Previous + Next as a tight nav pair (pure cursor)
            Mid   — Skip pad (action)
            Right — Finish pad (action)
          Disable conditions:
            Previous → at first pad
            Next     → at last pad
            Skip / Finish → any inflight on this pad, OR pad is in a
              terminal status (skipped / completed). Server enforces
              the same with 409; muting here saves a round trip and
              gives a clearer signal than an error banner.

          Sticky-bottom: pins to the viewport bottom when the scroll
          container (PadRekkoPanel's body, `flex-1 overflow-y-auto`)
          overflows. Sits naturally at the end of content otherwise.
          The bg-slate-900 matches the panel's base color so scrolled
          cards don't bleed through; border-t separates it from the
          scroll content above. */}
      <div className="sticky bottom-0 z-10 bg-slate-900 border-t border-slate-800/80
                      px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          {/* Nav pair — tight, no server effects */}
          <div className="flex items-center gap-1">
            <button
              onClick={back}
              disabled={isFirstPad}
              className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border transition
                          ${isFirstPad
                            ? "bg-slate-800/20 border-slate-800/60 text-slate-600 cursor-not-allowed"
                            : "bg-slate-800/40 border-slate-700/60 text-slate-300 hover:bg-slate-800 hover:border-slate-600"}`}
              title={isFirstPad ? "Already on the first pad" : "Go back to the previous pad"}
            >
              <ChevronLeft size={11} />
              Prev
            </button>
            <button
              onClick={forward}
              disabled={isLastPad}
              className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border transition
                          ${isLastPad
                            ? "bg-slate-800/20 border-slate-800/60 text-slate-600 cursor-not-allowed"
                            : "bg-slate-800/40 border-slate-700/60 text-slate-300 hover:bg-slate-800 hover:border-slate-600"}`}
              title={isLastPad ? "Last pad in this run" : "Move to the next pad without changing state"}
            >
              Next
              <ChevronRight size={11} />
            </button>
          </div>

          {/* Skip — action, audit-trail event */}
          <button
            onClick={() => skipPad({ padIdx })}
            disabled={!canSkip}
            className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border transition
                        ${canSkip
                          ? "bg-slate-800/40 border-slate-700/60 text-slate-300 hover:bg-slate-800 hover:border-slate-600"
                          : "bg-slate-800/20 border-slate-800/60 text-slate-600 cursor-not-allowed"}`}
            title={
              isSkippingThisPad   ? "Marking pad skipped…"
              : isFinishingThisPad ? "Finishing in progress — wait"
              : isAnalyzingThisPad ? "Analyzing finding — wait"
              : isTerminalPad      ? `Pad already ${pad.status} — no further action available`
              : "Mark this pad skipped and advance"
            }
          >
            {isSkippingThisPad
              ? <Loader2 size={11} className="animate-spin" />
              : <SkipForward size={11} />}
            {isSkippingThisPad ? "Skipping…" : "Skip"}
          </button>
        </div>

        <button
          onClick={() => finishPad({ padIdx })}
          disabled={!canFinish}
          className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border transition
                      ${canFinish
                        ? "bg-amber-500/15 border-amber-500/40 text-amber-200 hover:bg-amber-500/25"
                        : "bg-amber-500/5 border-amber-500/20 text-amber-200/40 cursor-not-allowed"}`}
          title={
            isFinishingThisPad   ? "Finishing pad — AI is generating the summary"
            : isSkippingThisPad   ? "Skipping in progress — wait"
            : isAnalyzingThisPad  ? "Analyzing finding — wait"
            : isTerminalPad       ? `Pad already ${pad.status} — no further action available`
            : "Finish this pad — generates a summary and advances"
          }
        >
          {isFinishingThisPad
            ? <Loader2 size={11} className="animate-spin" />
            : <Check size={11} />}
          {isFinishingThisPad ? "Finishing…" : "Finish"}
        </button>
      </div>
    </div>
  );
}
